import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { CustomerCard } from '../../components/CustomerCard';
import { Field, inputStyle } from '../../components/Field';
import { FieldPhotoUploader } from '../../components/FieldPhotoUploader';
import { NotesPanel } from '../../components/NotesPanel';
import { PhotoGrid } from '../../components/PhotoGrid';
import { Spinner } from '../../components/Spinner';
import { StatusChip } from '../../components/StatusChip';
import { apiGet, apiSend, ClientApiError } from '../../api/client';
import { formatDateTime } from '../../lib/format';
import type {
  ReconTeam,
  Referral,
  ReferralEvent,
  ReferralNote,
  ReferralPhoto,
} from '../../../shared/types';

interface Detail {
  referral: Referral;
  notes: ReferralNote[];
  photos: ReferralPhoto[];
  events: ReferralEvent[];
  techName: string | null;
  teamName: string | null;
}

interface AdminUser {
  id: string;
  fullName: string;
  role: string;
  status: string;
}

export function DispatchReferralScreen() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [techs, setTechs] = useState<AdminUser[]>([]);
  const [teams, setTeams] = useState<ReconTeam[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await apiGet<Detail>(`/api/referrals/${id}`));
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'Could not load that referral.');
    }
  }, [id]);

  useEffect(() => {
    void load();
    apiGet<{ users: AdminUser[] }>('/api/users?role=tech&status=active')
      .then((data) => setTechs(data.users))
      .catch(() => undefined);
    apiGet<{ teams: ReconTeam[] }>('/api/teams')
      .then((data) => setTeams(data.teams.filter((t) => t.active)))
      .catch(() => undefined);
  }, [load]);

  async function act(path: string, body: unknown) {
    setBusy(true);
    setError('');
    try {
      await apiSend(`/api/referrals/${id}/${path}`, 'PATCH', body);
      await load();
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (error && !detail) {
    return (
      <AppShell title="Referral">
        <div className="error-banner">{error}</div>
      </AppShell>
    );
  }
  if (!detail) {
    return (
      <AppShell title="Referral">
        <Spinner />
      </AppShell>
    );
  }

  const { referral } = detail;
  const open = referral.status !== 'complete' && referral.status !== 'declined';

  // A disabled tech or team is dropped from the fetched options list, which
  // would otherwise leave the <select> rendering blank and the job reading
  // as unassigned even though assigned_tech_id / recon_team_id are still
  // set. Compare against the options actually on hand so the gap is visible
  // and reassignment stays possible.
  const assignedTechMissing =
    referral.assignedTechId !== null && !techs.some((t) => t.id === referral.assignedTechId);
  const assignedTeamMissing =
    referral.reconTeamId !== null && !teams.some((t) => t.id === referral.reconTeamId);

  const techHint = detail.techName
    ? assignedTechMissing
      ? `Currently assigned to ${detail.techName}, who is no longer active. Reassign to continue.`
      : `Currently assigned to ${detail.techName}.`
    : undefined;

  const teamHint = !referral.assignedTechId
    ? 'Assign a field tech first.'
    : detail.teamName
      ? assignedTeamMissing
        ? `Currently assigned to ${detail.teamName}, which is no longer active. Reassign to continue.`
        : `Currently assigned to ${detail.teamName}.`
      : undefined;

  return (
    <AppShell title={referral.refNumber}>
      {error && <div className="error-banner">{error}</div>}

      <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <strong style={{ color: 'var(--navy)' }}>Status</strong>
        <StatusChip status={referral.status} />
      </div>

      <CustomerCard referral={referral} />

      <div className="card">
        <h2>Routing</h2>
        <Field label="Field tech" htmlFor="tech" hint={techHint}>
          <select
            id="tech"
            style={inputStyle}
            disabled={!open || busy}
            value={referral.assignedTechId ?? ''}
            onChange={(e) => e.target.value && void act('assign', { techId: e.target.value })}
          >
            <option value="" disabled>
              Choose a field tech
            </option>
            {techs.map((tech) => (
              <option key={tech.id} value={tech.id}>
                {tech.fullName}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Recon team" htmlFor="team" hint={teamHint}>
          <select
            id="team"
            style={inputStyle}
            disabled={!open || busy || !referral.assignedTechId}
            value={referral.reconTeamId ?? ''}
            onChange={(e) => e.target.value && void act('recon', { teamId: e.target.value })}
          >
            <option value="" disabled>
              Choose a recon team
            </option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {open && (
        <div className="card stack">
          <Button
            full
            disabled={busy || !referral.assignedTechId}
            onClick={() => void act('status', { status: 'complete' })}
          >
            Mark complete
          </Button>

          {!showDecline ? (
            <Button variant="danger" full disabled={busy} onClick={() => setShowDecline(true)}>
              Decline referral
            </Button>
          ) : (
            <div>
              <Field
                label="Reason for declining"
                htmlFor="reason"
                hint="Optional. The referring plumber will see it."
              >
                <input
                  id="reason"
                  style={inputStyle}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                />
              </Field>
              <div className="row">
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => void act('status', { status: 'declined', reason: declineReason })}
                >
                  Confirm decline
                </Button>
                <Button variant="secondary" onClick={() => setShowDecline(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {referral.status === 'declined' && referral.declineReason && (
        <div className="card">
          <h2>Declined</h2>
          <p style={{ margin: 0 }}>{referral.declineReason}</p>
        </div>
      )}

      <div className="card">
        <h2>Photos</h2>
        <PhotoGrid photos={detail.photos} />
        {open && (
          <FieldPhotoUploader
            referralId={referral.id}
            onUploaded={(photo) =>
              setDetail((prev) => (prev ? { ...prev, photos: [...prev.photos, photo] } : prev))
            }
            onReload={load}
          />
        )}
      </div>

      <NotesPanel
        referralId={referral.id}
        notes={detail.notes}
        canAdd={open}
        onAdded={(note) =>
          setDetail((prev) => (prev ? { ...prev, notes: [...prev.notes, note] } : prev))
        }
        onReload={load}
      />

      <div className="card">
        <h2>History</h2>
        {detail.events.map((event) => (
          <p key={event.id} className="muted" style={{ margin: '0.2rem 0' }}>
            {formatDateTime(event.createdAt)} · {event.type.replace(/_/g, ' ')} · {event.actorName}
            {event.detail ? ` · ${event.detail}` : ''}
          </p>
        ))}
      </div>
    </AppShell>
  );
}
